import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarDays, BarChart3, Wifi, Upload, Bell } from "lucide-react";

const features = [
  { icon: CalendarDays, text: "Track every gig — log shows, fees, and dates in one place" },
  { icon: BarChart3, text: "Know your numbers — see income, venues, and stats at a glance" },
  { icon: Wifi, text: "Works offline — use it from your home screen, no app store needed" },
  { icon: Upload, text: "Export for Songkick and Bandsintown — perfectly formatted CSV, ready to upload" },
];

export default function WelcomePage() {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const [notifState, setNotifState] = useState("pending"); // pending | granted | dismissed
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  useEffect(() => {
    document.title = "Welcome — Fretless";
    const timer = setTimeout(() => setShowContent(true), reducedMotion ? 0 : 800);
    return () => clearTimeout(timer);
  }, [reducedMotion]);

  const handleAllowNotifications = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      setNotifState(permission === "granted" ? "granted" : "dismissed");
    } else {
      setNotifState("dismissed");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="welcome-page">
      <div className="max-w-md w-full text-center">
        {/* Animated checkmark */}
        <div className="relative w-24 h-24 mx-auto mb-8">
          <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden="true">
            <circle
              cx="50" cy="50" r="44"
              fill="none"
              stroke="var(--color-orange)"
              strokeWidth="4"
              strokeDasharray="276.46"
              strokeDashoffset={reducedMotion ? "0" : "276.46"}
              style={!reducedMotion ? { animation: "circle-draw 0.6s ease-out forwards" } : {}}
            />
            <path
              d="M30 52 L44 66 L70 38"
              fill="none"
              stroke="var(--color-orange)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="60"
              strokeDashoffset={reducedMotion ? "0" : "60"}
              style={!reducedMotion ? { animation: "check-draw 0.4s ease-out 0.5s forwards" } : {}}
            />
          </svg>
        </div>

        <h1
          className={`text-3xl font-bold text-white mb-2 transition-all duration-500 ${showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
        >
          You're all set!
        </h1>
        <p
          className={`text-neutral-400 mb-10 transition-all duration-500 delay-100 ${showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
        >
          Welcome to Fretless. Here's what you can do:
        </p>

        {/* Feature highlights */}
        <div className="space-y-4 mb-8 text-left">
          {features.map((f, i) => (
            <div
              key={i}
              className={`flex items-start gap-4 p-3 rounded-lg bg-neutral-900/60 transition-all duration-500 ${showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              style={{ transitionDelay: `${(i + 2) * 100}ms` }}
            >
              <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm text-neutral-300 pt-2">{f.text}</p>
            </div>
          ))}
        </div>

        {/* Push notification prompt */}
        {notifState === "pending" && "Notification" in window && (
          <div
            className={`mb-6 p-4 rounded-lg bg-neutral-900/80 border border-neutral-800 transition-all duration-500 ${showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            style={{ transitionDelay: "500ms" }}
            data-testid="notification-prompt"
          >
            <div className="flex items-center gap-3 mb-3">
              <Bell className="h-5 w-5 text-primary" />
              <p className="text-sm text-neutral-300">Want reminders for upcoming gigs?</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleAllowNotifications}
                className="flex-1 bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90"
                data-testid="notification-allow"
              >
                Allow
              </Button>
              <Button
                variant="outline"
                onClick={() => setNotifState("dismissed")}
                className="flex-1 text-sm border-neutral-700 text-neutral-400 hover:text-white"
                data-testid="notification-later"
              >
                Maybe later
              </Button>
            </div>
          </div>
        )}

        {notifState === "granted" && (
          <p className="text-sm text-primary mb-6" data-testid="notification-granted">Notifications enabled!</p>
        )}

        <Button
          onClick={() => navigate("/dashboard", { replace: true })}
          className={`w-full py-6 text-base bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-all duration-500 ${showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "600ms" }}
          data-testid="welcome-go-dashboard"
        >
          Go to my dashboard
        </Button>
      </div>

      <style>{`
        @keyframes circle-draw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes check-draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}
